import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './AssistantConfiguration.css';
import ConfigurationModule from './ConfigurationModule';
import UnifiedChat from '../UnifiedChat/UnifiedChat';
import ErrorBoundary from '../../components/ErrorBoundary';
import { useLocation } from 'react-router-dom';
import { formatAssistantData } from '../../utils/formatAssistantData';
import InstructionPanel from '../../components/InstructionPanel';

function AssistantConfiguration() {
    const { t: tAssistantConfiguration } = useTranslation('assistantConfiguration');
    const location = useLocation();
    const assistantData = location.state?.assistantData || {}; // Garante que assistantData não seja undefined
    const user = location.state?.user || {};
    const email = location.state?.email || {};
    const sessionId = location.state?.sessionId || {};
    const exists = location.state?.exists || false;
    const [model, setModel] = useState('gemini-3.5-flash-lite'); // Estado para armazenar o modelo llm
    const [assistantId, setAssistantId] = useState(assistantData.data?._id || null); // ID do assistente
    const [storageUsage, setStorageUsage] = useState(assistantData.data?.storage_usage || { indexed_tokens: 0}); // Estado para uso de armazenamento
    

    // Definição do estado inicial
    const [assistant, setAssistant] = useState(() => formatAssistantData(assistantData, user));
    const [documentList, setDocumentList] = useState(assistant.database || []);
    const [users, setUsers] = useState(assistantData?.users || []);
    const [status, setStatus] = useState(assistantData?.status || false);
    const [svgPath, setSvgPath] = useState("");
    const [isNewCreated, setIsNewCreated] = useState(exists);
    const [chatHistory, setChatHistory] = useState([]);
    // Tarefa 1.1 (specs/updated_self_assistant/plan.md): mesmo sinal
    // `createSkillSignal` que o "Criar com Iris" do chat geral usa
    // (pages/Chat/index.js) - "Criar Skill" na aba Skills incrementa aqui,
    // consumido pelo UnifiedChat (useTestBox) via prop, que ja' insere
    // "/skill-creator" + texto de ajuda no campo de mensagem.
    const [createSkillSignal, setCreateSkillSignal] = useState(0);


    const [validationErrors, setValidationErrors] = useState({
        name: false,
        description: false
    });
    const [showValidationErrors, setShowValidationErrors] = useState(false);

    const [summaryInfo, setSummaryInfo] = useState({
        label: tAssistantConfiguration('newChat'),
        status: false,
        session_id: sessionId
    });

     // Atualiza `assistant` sempre que `assistantData` mudar
     useEffect(() => {
        if (exists){
        setAssistant(formatAssistantData(assistantData, user));
        setDocumentList(assistantData.data?.database || []);
        setUsers(assistantData.data?.users || []);
        setStatus(assistantData.data?.status || false);
        setSvgPath(assistantData.data?.assistant.icon || "");
        setAssistantId(assistantData.data?._id || null);
        setStorageUsage(assistantData.data?.storage_usage || { indexed_tokens: 0 });
        }
    }, [assistantData]); // Dependências garantem atualização correta

    // validate required fields. accepts optional assistant param for immediate validation
    const validateRequiredFields = (assistantToValidate = null) => {
        // Only name and description are required
        const a = assistantToValidate || assistant;
        const name = a.assistant?.name;
        const description = Array.isArray(a.assistant?.description)
            ? a.assistant.description[0]
            : a.assistant?.description;

        const nameEmpty = !name?.trim();
        const descriptionEmpty = !description?.trim();

        setValidationErrors({
            name: nameEmpty,
            description: descriptionEmpty
        });

        return !(nameEmpty || descriptionEmpty);
    };

    // Called when user attempts to navigate to restricted sections
    const triggerValidation = () => {
        // ensure validation state is up to date
        validateRequiredFields();
        // show the red error states
        setShowValidationErrors(true);
    };

    // Atualiza a estrutura do estado ao modificar qualquer campo
    const handleUpdate = (section, key, value) => {
        if (section === 'assistant' && key === 'icon') {
            setSvgPath(value);
        }
        // compute updated assistant and set state, then validate with the updated object
        setAssistant(prevState => {
            const updatedSection = {
                ...prevState[section],
                [key]: key === "description" ? [value] : value // keep description as array
            };

            const updatedAssistant = {
                ...prevState,
                [section]: updatedSection,
                updated_at: new Date().toISOString().split('T')[0]
            };

            // validate immediately using the updated assistant
            validateRequiredFields(updatedAssistant);

            return updatedAssistant;
        });
    };

    // Atualiza flags de capacidades do assistente (como web search)
    const handleCapabilitiesUpdate = (key, value) => {
        setAssistant(prevState => ({
            ...prevState,
            [key]: value,
            updated_at: new Date().toISOString().split('T')[0]
        }));
    };
    
    return(
        <div className='configuration-box'>

            <div className='configuration-margem'>

                <ConfigurationModule 
                classeCSS="configuration-module" 
                email={email}
                model={model}
                setModel={setModel}
                showValidationErrors={showValidationErrors}
                triggerValidation={triggerValidation}
                sessionId={sessionId}
                setAssistantId={setAssistantId}
                assistant={assistant}
                setAssistant={setAssistant}
                svgPath={svgPath}
                isNewCreated={isNewCreated}
                setIsNewCreated={setIsNewCreated}
                documentList={documentList}
                setDocumentList={setDocumentList}
                users={users}
                setUsers={setUsers}
                status={status}
                setStatus={setStatus}
                handleUpdate={handleUpdate}
                handleCapabilitiesUpdate={handleCapabilitiesUpdate}
                validationErrors={validationErrors}
                storageUsage={storageUsage}
                assistantId={assistantId}
                onCreateSkill={() => setCreateSkillSignal((signal) => signal + 1)}
                />

                

                <ErrorBoundary resetKeys={[sessionId, assistantId]} errorContext={{ sessionId, assistantId, email }}>
                    <UnifiedChat
                        useTestBox
                        classeCSS="test-box"
                        assistant={'self-assistant'}
                        assistantId={assistantId}
                        sessionId={sessionId}
                        email={email}
                        model={model}
                        setModel={setModel}
                        summaryInfo={summaryInfo}
                        setSummaryInfo={setSummaryInfo}
                        webSearchEnabled={assistant.web_search_enabled}
                        llmProvider={assistant.llm_provider}
                        createSkillSignal={createSkillSignal}
                    updateSummary={(updated) =>
                            setSummaryInfo(prev => ({ ...prev, ...updated }))
                        }
                    />
                </ErrorBoundary>

            </div>

        </div>
    );
}

export default AssistantConfiguration